import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FaPlus, 
  FaEdit, 
  FaTrash, 
  FaSearch,
  FaFilter,
  FaHandshake,
  FaEye,
  FaMoneyBillAlt
} from 'react-icons/fa';
import { partnerIncomeService, PartnerIncome, PartnerIncomeFilters } from '../services/partnerIncomeService';
import { projectService } from '../services/projectService';
import { partnerService } from '../services/partnerService';
import ConfirmDialog from '../components/ConfirmDialog';
import { usePageTitle } from '../hooks/usePageTitle';
import '../css/pages/PartnerLedger.css';

interface Project {
  id: number;
  name: string;
}

interface Partner {
  id: number;
  name: string;
}

const PartnerLedger: React.FC = () => {
  usePageTitle();
  const navigate = useNavigate();
  const [partnerIncomes, setPartnerIncomes] = useState<PartnerIncome[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [editingIncome, setEditingIncome] = useState<PartnerIncome | null>(null);
  const [selectedPartnerIncomes, setSelectedPartnerIncomes] = useState<PartnerIncome[]>([]);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [incomeToDelete, setIncomeToDelete] = useState<PartnerIncome | null>(null);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 20;
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [projectFilter, setProjectFilter] = useState('');
  const [partnerFilter, setPartnerFilter] = useState('');
  const [paymentMethodFilter, setPaymentMethodFilter] = useState('');
  const [transactionTypeFilter, setTransactionTypeFilter] = useState('');
  
  // Form data
  const [formData, setFormData] = useState({
    partnerId: '',
    projectId: '',
    amount: '',
    paymentDate: '',
    paymentMethod: '',
    referenceNumber: '',
    bankName: '',
    remarks: '',
    receiptName: '',
    transactionType: 'income'
  });

  useEffect(() => {
    fetchPartnerIncomes();
    fetchProjects();
    fetchPartners();
  }, [currentPage, searchTerm, statusFilter, projectFilter, partnerFilter, paymentMethodFilter, transactionTypeFilter]);

  const fetchPartnerIncomes = useCallback(async () => {
    try {
      setLoading(true);
      const filters: PartnerIncomeFilters = {
        page: currentPage,
        limit,
        ...(searchTerm && { search: searchTerm }),
        ...(statusFilter && { status: statusFilter }),
        ...(projectFilter && { project: projectFilter }),
        ...(partnerFilter && { partner: partnerFilter }),
        ...(paymentMethodFilter && { paymentMethod: paymentMethodFilter }),
        ...(transactionTypeFilter && { transactionType: transactionTypeFilter })
      };
      
      const response = await partnerIncomeService.getPartnerIncomes(filters);
      setPartnerIncomes(response.partnerIncomes);
      setTotal(response.pagination.total);
      setTotalPages(response.pagination.totalPages);
    } catch (error) {
      console.error('Error fetching partner incomes:', error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, statusFilter, projectFilter, partnerFilter, paymentMethodFilter, transactionTypeFilter, limit]);

  const fetchProjects = async () => {
    try {
      const response = await projectService.getProjects();
      setProjects(response);
      
      // Auto-select first project
      if (response.length > 0) {
        setProjectFilter(response[0].id.toString());
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const fetchPartners = async () => {
    try {
      const response = await partnerService.getPartners();
      setPartners(response.partners);
    } catch (error) {
      console.error('Error fetching partners:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      partnerId: '',
      projectId: '',
      amount: '',
      paymentDate: '',
      paymentMethod: '',
      referenceNumber: '',
      bankName: '',
      remarks: '',
      receiptName: '',
      transactionType: 'income'
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const data = {
        partnerId: parseInt(formData.partnerId),
        projectId: parseInt(formData.projectId),
        amount: parseFloat(formData.amount),
        paymentDate: formData.paymentDate,
        paymentMethod: formData.paymentMethod as any,
        referenceNumber: formData.referenceNumber || undefined,
        bankName: formData.bankName || undefined,
        remarks: formData.remarks || undefined,
        receiptName: formData.receiptName || undefined,
        transactionType: formData.transactionType as any
      };

      if (editingIncome) {
        await partnerIncomeService.updatePartnerIncome(editingIncome.id, data);
      } else {
        await partnerIncomeService.createPartnerIncome(data);
      }
      
      setShowModal(false);
      setEditingIncome(null);
      resetForm();
      fetchPartnerIncomes();
    } catch (error) {
      console.error('Error saving partner income:', error);
    }
  };

  const handleEdit = (income: PartnerIncome) => {
    // Navigate to edit page - to be implemented
  };

  const handleDelete = (income: PartnerIncome) => {
    setIncomeToDelete(income);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (incomeToDelete) {
      try {
        await partnerIncomeService.deletePartnerIncome(incomeToDelete.id);
        setShowDeleteDialog(false);
        setIncomeToDelete(null);
        fetchPartnerIncomes();
      } catch (error) {
        console.error('Error deleting partner income:', error);
      }
    }
  };

  const handleViewHistory = async (income: PartnerIncome) => {
    try {
      const incomes = await partnerIncomeService.getPartnerIncomesByPartner(income.partnerId);
      setSelectedPartnerIncomes(incomes);
      setShowHistoryModal(true);
    } catch (error) {
      console.error('Error fetching partner income history:', error);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleStatusFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setStatusFilter(e.target.value);
    setCurrentPage(1);
  };

  const handleProjectFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setProjectFilter(e.target.value);
    setCurrentPage(1);
  };

  const handlePartnerFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setPartnerFilter(e.target.value);
    setCurrentPage(1);
  };

  const handlePaymentMethodFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setPaymentMethodFilter(e.target.value);
    setCurrentPage(1);
  };

  const handleTransactionTypeFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setTransactionTypeFilter(e.target.value);
    setCurrentPage(1);
  };

  if (loading && partnerIncomes.length === 0) {
    return <div className="loading">Loading partner ledger...</div>;
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
          <FaHandshake style={{fontSize: '24px', color: '#3b82f6'}} />
          <h1 style={{margin: '0', color: '#1f2937', fontSize: '28px', fontWeight: '700'}}>Partner Ledger</h1>
        </div>
        <div style={{display: 'flex', gap: '12px', alignItems: 'center'}}>
          <button 
            className="btn btn-primary"
            onClick={() => navigate('/ledger/partner/add')}
          >
            <FaPlus /> Add Transaction
          </button>
        </div>
      </div>

      <div className="partner-ledger-controls">
        <div className="search-input-container">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search by partner name, project, receipt name..."
            value={searchTerm}
            onChange={handleSearchChange}
            className="form-input"
          />
        </div>
        
        <div className="filter-bar">
          <FaFilter />
          <select value={statusFilter} onChange={handleStatusFilterChange}>
            <option value="">All Status</option>
            {partnerIncomeService.getStatusOptions().map(status => (
              <option key={status.value} value={status.value}>
                {status.label}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-bar">
          <select value={transactionTypeFilter} onChange={handleTransactionTypeFilterChange}>
            <option value="">All Transaction Types</option>
            {partnerIncomeService.getTransactionTypes().map(type => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-bar">
          <select value={projectFilter} onChange={handleProjectFilterChange}>
            <option value="">All Projects</option>
            {projects.map(project => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-bar">
          <select value={partnerFilter} onChange={handlePartnerFilterChange}>
            <option value="">All Partners</option>
            {partners.map(partner => (
              <option key={partner.id} value={partner.id}>
                {partner.name}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-bar">
          <select value={paymentMethodFilter} onChange={handlePaymentMethodFilterChange}>
            <option value="">All Payment Methods</option>
            {partnerIncomeService.getPaymentMethods().map(method => (
              <option key={method.value} value={method.value}>
                {method.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="content-section">
        <table className="standard-table">
          <thead>
            <tr>
              <th>Sr. No</th>
              <th>Partner</th>
              <th>Project</th>
              <th>Transaction Type</th>
              <th>Receipt Name</th>
              <th>Amount</th>
              <th>Payment Date</th>
              <th>Payment Method</th>
              <th>Reference</th>
              <th>Clearance Date</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {partnerIncomes.map((income, index) => (
              <tr key={income.id}>
                <td style={{ fontWeight: "600", color: "#4f46e5" }}>
                  {partnerIncomes.length - index}
                </td>
                <td>
                  <span className="partner-name">{income.partner.name}</span>
                </td>
                <td>
                  <span className="project-name">{income.project.name}</span>
                </td>
                <td>
                  <span className={`transaction-type ${partnerIncomeService.getTransactionTypeDisplay(income.transactionType).className}`}>
                    {partnerIncomeService.getTransactionTypeDisplay(income.transactionType).text}
                  </span>
                </td>
                <td>
                  <span className="receipt-name">{income.receiptName || '-'}</span>
                </td>
                <td>
                  <span className={`amount ${income.transactionType === 'income' ? 'income-amount' : 'disbursement-amount'}`}>
                    {income.transactionType === 'income' ? '+' : '-'}{partnerIncomeService.formatAmount(income.amount)}
                  </span>
                </td>
                <td>
                  <span className="payment-date">{partnerIncomeService.formatDate(income.paymentDate)}</span>
                </td>
                <td>
                  <span className="payment-method">
                    {partnerIncomeService.getPaymentMethodDisplay(income.paymentMethod)}
                  </span>
                </td>
                <td>
                  <span className="reference">{income.referenceNumber || '-'}</span>
                </td>
                <td>
                  <span className="clearance-date">
                    {income.chequeClearanceDate ? partnerIncomeService.formatDate(income.chequeClearanceDate) : '-'}
                  </span>
                </td>
                <td>
                  <span className={`status ${partnerIncomeService.getStatusDisplay(income.status).className}`}>
                    {partnerIncomeService.getStatusDisplay(income.status).text}
                  </span>
                </td>
                <td>
                  <div className="partner-income-actions">
                    <button 
                      className="btn-icon view"
                      onClick={() => handleViewHistory(income)}
                      title="View Partner History"
                    >
                      <FaEye />
                    </button>
                    {income.status === 'approval_pending' && (
                      <>
                        <button 
                          className="btn-icon edit"
                          onClick={() => handleEdit(income)}
                          title="Edit Transaction"
                        >
                          <FaEdit />
                        </button>
                        <button 
                          className="btn-icon delete"
                          onClick={() => handleDelete(income)}
                          title="Delete Transaction"
                        >
                          <FaTrash />
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {partnerIncomes.length === 0 && !loading && (
        <div className="empty-state">
          <FaHandshake className="empty-icon" />
          <h3>No Partner Transactions Found</h3>
          <p>Start by adding your first partner transaction</p>
          <button 
            className="add-partner-income-btn"
            onClick={() => navigate('/ledger/partner/add')}
          >
            <FaPlus /> Add Transaction
          </button>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            className="page-btn"
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(currentPage - 1)}
          >
            Previous
          </button>
          
          <div className="page-numbers">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const startPage = Math.max(1, currentPage - 2);
              const page = Math.min(startPage + i, totalPages);
              
              if (page > totalPages) return null;
              
              return (
                <button
                  key={page}
                  className={`page-btn ${currentPage === page ? 'active' : ''}`}
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </button>
              );
            }).filter(Boolean)}
          </div>
          
          <button
            className="page-btn"
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage(currentPage + 1)}
          >
            Next
          </button>
        </div>
      )}

      {/* Partner Transaction History Modal */}
      {showHistoryModal && (
        <div className="modal-overlay">
          <div className="modal large">
            <div className="modal-header">
              <h2>Partner Transaction History</h2>
              <button 
                className="close-btn"
                onClick={() => setShowHistoryModal(false)}
              >
                ×
              </button>
            </div>
            
            <div className="modal-content">
              <div className="content-section">
                <table className="standard-table">
                  <thead>
                    <tr>
                      <th>Sr. No</th>
                      <th>Project</th>
                      <th>Transaction Type</th>
                      <th>Receipt Name</th>
                      <th>Amount</th>
                      <th>Payment Date</th>
                      <th>Method</th>
                      <th>Reference</th>
                      <th>Clearance Date</th>
                      <th>Status</th>
                      <th>Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedPartnerIncomes.map((income, index) => (
                      <tr key={income.id}>
                        <td style={{ fontWeight: "600", color: "#4f46e5" }}>
                          {selectedPartnerIncomes.length - index}
                        </td>
                        <td>{income.project.name}</td>
                        <td>
                          <span className={`transaction-type ${partnerIncomeService.getTransactionTypeDisplay(income.transactionType).className}`}>
                            {partnerIncomeService.getTransactionTypeDisplay(income.transactionType).text}
                          </span>
                        </td>
                        <td>{income.receiptName || '-'}</td>
                        <td>
                          <span className={`amount ${income.transactionType === 'income' ? 'income-amount' : 'disbursement-amount'}`}>
                            {income.transactionType === 'income' ? '+' : '-'}{partnerIncomeService.formatAmount(income.amount)}
                          </span>
                        </td>
                        <td>{partnerIncomeService.formatDate(income.paymentDate)}</td>
                        <td>{partnerIncomeService.getPaymentMethodDisplay(income.paymentMethod)}</td>
                        <td>{income.referenceNumber || '-'}</td>
                        <td>{income.chequeClearanceDate ? partnerIncomeService.formatDate(income.chequeClearanceDate) : '-'}</td>
                        <td>
                          <span className={`status ${partnerIncomeService.getStatusDisplay(income.status).className}`}>
                            {partnerIncomeService.getStatusDisplay(income.status).text}
                          </span>
                        </td>
                        <td>{partnerIncomeService.formatDate(income.createdAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        title="Delete Partner Transaction"
        message={`Are you sure you want to delete this partner transaction of ${incomeToDelete ? partnerIncomeService.formatAmount(incomeToDelete.amount) : ''}? This action cannot be undone.`}
        onConfirm={confirmDelete}
        onCancel={() => {
          setShowDeleteDialog(false);
          setIncomeToDelete(null);
        }}
      />
    </div>
  );
};

export default PartnerLedger;
